import { useEffect } from "react";

/**
 * Application-wide spatial keyboard navigation.
 *
 * Arrow keys move focus to the visually nearest focusable control in the
 * pressed direction (left/right/up/down), across regions (e.g. from main
 * content to the sidebar). This is an *additional* navigation method:
 * Tab / Shift+Tab order is untouched, no tabindex values are rewritten,
 * and no keyboard trap is created.
 *
 * Safety rules (see the guard functions below):
 * - never runs while focus is in a text-editing context or any composite
 *   widget that owns its own Arrow-key pattern (select, combobox, listbox,
 *   menu, tabs, tree, grid, radiogroup, slider, date picker, editors, …)
 * - never runs with Alt/Ctrl/Meta/Shift held
 * - never runs when another handler already handled the event
 *   (defaultPrevented)
 * - elements/subtrees can opt out with data-spatial-nav="off"
 * - when a modal dialog is open, candidates are confined to the dialog so
 *   focus cannot escape the modal.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "[tabindex]",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
].join(",");

/** Roles whose ARIA pattern owns Arrow keys — never intercept inside them. */
const ARROW_OWNING_ROLES = new Set([
  "combobox",
  "listbox",
  "option",
  "menu",
  "menubar",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "tablist",
  "tab",
  "tree",
  "treegrid",
  "treeitem",
  "grid",
  "gridcell",
  "row",
  "radiogroup",
  "radio",
  "slider",
  "spinbutton",
  "scrollbar",
  "toolbar",
]);

/** Input types where Arrow keys move the caret or change the value. */
const ARROW_EDITING_INPUT_TYPES = new Set([
  "text",
  "search",
  "url",
  "tel",
  "email",
  "password",
  "number",
  "range",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
  "color",
]);

type Direction = "left" | "right" | "up" | "down";

const KEY_TO_DIR: Record<string, Direction> = {
  ArrowLeft: "left",
  ArrowRight: "right",
  ArrowUp: "up",
  ArrowDown: "down",
};

/** True when spatial navigation must NOT handle this event. */
function shouldIgnore(e: KeyboardEvent, el: HTMLElement): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return true;
  if (e.defaultPrevented) return true;

  // Explicit opt-out on the element or any ancestor.
  if (el.closest('[data-spatial-nav="off"]')) return true;

  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    if (ARROW_EDITING_INPUT_TYPES.has(type)) return true;
    // radio participates in a native radiogroup arrow pattern
    if (type === "radio") return true;
  }
  if (el.isContentEditable) return true;
  if (tag === "AUDIO" || tag === "VIDEO") return true;

  // Composite widgets that own Arrow keys — check self and ancestors.
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    const role = node.getAttribute("role");
    if (role && ARROW_OWNING_ROLES.has(role)) return true;
    // Radix popper-positioned content (dropdowns, selects, menus,
    // popovers) manages its own focus movement.
    if (node.hasAttribute("data-radix-popper-content-wrapper")) return true;
    // Code editors (CodeMirror/Monaco) and rich text editors.
    if (
      node.classList.contains("cm-editor") ||
      node.classList.contains("monaco-editor") ||
      node.classList.contains("ProseMirror") ||
      node.classList.contains("ql-editor") ||
      node.classList.contains("tiptap")
    ) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** The container that bounds candidate search (open modal dialog or document). */
function getScopeRoot(el: HTMLElement): HTMLElement {
  const dialog = el.closest<HTMLElement>(
    '[role="dialog"], [role="alertdialog"], dialog',
  );
  return dialog ?? document.body;
}

function isVisible(el: HTMLElement): boolean {
  if (el.closest('[aria-hidden="true"], [inert], [data-spatial-nav="off"]')) return false;
  // checkVisibility covers display/visibility/content-visibility.
  if (typeof (el as any).checkVisibility === "function") {
    if (!(el as any).checkVisibility()) return false;
  } else if (!el.getClientRects().length) {
    return false;
  }
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  return true;
}

function isEligible(el: HTMLElement): boolean {
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.getAttribute("aria-disabled") === "true") return false;
  const tabindex = el.getAttribute("tabindex");
  if (tabindex !== null && parseInt(tabindex, 10) < 0) {
    // Negative tabindex is not reachable via keyboard navigation —
    // skip unless it is the currently focused element (handled by caller).
    return false;
  }
  // Elements matched only by [tabindex] must still be genuinely interactive
  // or explicitly focusable; the selector already limits to sensible cases.
  return isVisible(el);
}

function collectCandidates(scope: HTMLElement, current: HTMLElement): HTMLElement[] {
  const all = scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  const out: HTMLElement[] = [];
  all.forEach((el) => {
    if (el === current) return;
    if (current.contains(el) || el.contains(current)) return;
    if (!isEligible(el)) return;
    out.push(el);
  });
  return out;
}

function regionOf(el: HTMLElement): string | null {
  const region = el.closest<HTMLElement>("[data-focus-region]");
  return region ? region.getAttribute("data-focus-region") : null;
}

/** Nearest scrollable ancestor (acts as an implicit focus region so list
 * traversal doesn't "escape" a scroll container mid-way). */
function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    if (isScrollable(parent)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Score a candidate for a direction. Lower is better; null when the
 * candidate is not in the requested direction.
 */
function scoreCandidate(
  cur: DOMRect,
  cand: DOMRect,
  dir: Direction,
  sameRegion: boolean,
  sameScrollContainer: boolean,
): number | null {
  const curCx = cur.left + cur.width / 2;
  const curCy = cur.top + cur.height / 2;
  const candCx = cand.left + cand.width / 2;
  const candCy = cand.top + cand.height / 2;

  let primary: number; // distance along the pressed axis (must be > 0)
  let orthoGap: number; // gap between orthogonal ranges (0 when overlapping)
  let orthoCenter: number; // orthogonal center offset (alignment tie-break)

  if (dir === "left" || dir === "right") {
    primary = dir === "right" ? candCx - curCx : curCx - candCx;
    orthoGap = Math.max(cand.top - cur.bottom, cur.top - cand.bottom, 0);
    orthoCenter = Math.abs(candCy - curCy);
  } else {
    primary = dir === "down" ? candCy - curCy : curCy - candCy;
    orthoGap = Math.max(cand.left - cur.right, cur.left - cand.right, 0);
    orthoCenter = Math.abs(candCx - curCx);
  }

  if (primary <= 1) return null; // not in that direction

  // Overlapping on the orthogonal axis (same row/column) is strongly
  // preferred; otherwise the range gap dominates so we pick the closest
  // visually aligned control in the next row/column.
  let score = primary + orthoGap * 4 + orthoCenter * 0.5;
  if (!sameRegion) score += 40; // gentle preference for staying in-region
  // Strong preference for staying inside the same scroll container so a
  // list traversal doesn't escape to nearby outside controls mid-way.
  if (!sameScrollContainer) score += 120;
  return score;
}

function moveFocus(dir: Direction, current: HTMLElement): boolean {
  const scope = getScopeRoot(current);
  const candidates = collectCandidates(scope, current);
  if (!candidates.length) return false;

  const curRect = current.getBoundingClientRect();
  const curRegion = regionOf(current);
  const curScroll = scrollContainerOf(current);

  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const cand of candidates) {
    const rect = cand.getBoundingClientRect();
    const score = scoreCandidate(
      curRect,
      rect,
      dir,
      regionOf(cand) === curRegion,
      scrollContainerOf(cand) === curScroll,
    );
    if (score !== null && score < bestScore) {
      bestScore = score;
      best = cand;
    }
  }
  if (!best) return false;

  best.focus({ preventScroll: true });
  if (document.activeElement !== best) return false;

  ensureVisibleInScrollAncestors(best);
  return true;
}

/** Gap kept between the focused item and the scroll container's edges so the
 * full focus ring stays visible. */
const SCROLL_MARGIN = 12;

function isScrollable(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight && el.scrollWidth <= el.clientWidth) {
    return false;
  }
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  const ox = style.overflowX;
  return (
    oy === "auto" || oy === "scroll" || ox === "auto" || ox === "scroll"
  );
}

/**
 * Bring `el` fully into view inside each scrollable ancestor it actually
 * belongs to (nearest first), scrolling only the containers that clip it —
 * never a blanket `scrollIntoView()` (which can jerk the whole page) and
 * never the document unless the element is genuinely outside the viewport.
 */
function ensureVisibleInScrollAncestors(el: HTMLElement): void {
  let parent = el.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    if (isScrollable(parent)) {
      const cRect = parent.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const margin = Math.min(
        SCROLL_MARGIN,
        Math.max(0, (parent.clientHeight - r.height) / 2),
      );

      if (parent.scrollHeight > parent.clientHeight) {
        if (r.top < cRect.top + margin) {
          parent.scrollTop -= cRect.top + margin - r.top;
        } else if (r.bottom > cRect.bottom - margin) {
          parent.scrollTop += r.bottom - (cRect.bottom - margin);
        }
      }
      if (parent.scrollWidth > parent.clientWidth) {
        const hMargin = Math.min(
          SCROLL_MARGIN,
          Math.max(0, (parent.clientWidth - r.width) / 2),
        );
        if (r.left < cRect.left + hMargin) {
          parent.scrollLeft -= cRect.left + hMargin - r.left;
        } else if (r.right > cRect.right - hMargin) {
          parent.scrollLeft += r.right - (cRect.right - hMargin);
        }
      }
    }
    parent = parent.parentElement;
  }

  // Finally, if the element is still outside the viewport itself, scroll the
  // document minimally.
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let dx = 0;
  let dy = 0;
  if (r.top < SCROLL_MARGIN) dy = r.top - SCROLL_MARGIN;
  else if (r.bottom > vh - SCROLL_MARGIN) dy = r.bottom - (vh - SCROLL_MARGIN);
  if (r.left < SCROLL_MARGIN) dx = r.left - SCROLL_MARGIN;
  else if (r.right > vw - SCROLL_MARGIN) dx = r.right - (vw - SCROLL_MARGIN);
  if (dx !== 0 || dy !== 0) window.scrollBy(dx, dy);
}

/**
 * Mount once at the application root. Adds a document-level Arrow-key
 * listener implementing spatial focus navigation with the safety rules
 * documented above.
 */
export function useSpatialFocusNavigation(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const dir = KEY_TO_DIR[e.key];
      if (!dir) return;

      const active = document.activeElement;
      // Nothing (or the page body) focused: leave Arrow keys to native
      // scrolling — never hijack plain page scroll.
      if (!active || active === document.body || active === document.documentElement) return;
      if (!(active instanceof HTMLElement)) return;

      if (shouldIgnore(e, active)) return;

      if (moveFocus(dir, active)) {
        e.preventDefault();
      }
      // When no candidate exists in that direction, fall through to the
      // browser default (e.g. scrolling) — no trap, no dead ends.
    };

    // Bubble phase so component-level handlers (Radix widgets, custom
    // grids, etc.) run first and can preventDefault to opt out.
    document.addEventListener("keydown", onKeyDown, false);
    return () => document.removeEventListener("keydown", onKeyDown, false);
  }, [enabled]);
}
